/**
 * Results Service - Reporting data fetching
 *
 * Fetches scan results from the server for reporting.
 * Note: Server returns only sheet numbers, NOT student names.
 * Names are merged client-side from local SQLite database.
 */

import { api } from './api-client';

// =============================================================================
// Types - Backend Response (snake_case)
// =============================================================================

interface ScanResultResponse {
  scanId: string;
  sheetNumber: string;
  classId: string | null;
  totalScore: number;
  maxScore: number;
  percentage: number;
  requiresReview: boolean;
  reviewedByTeacher: boolean;
  createdAt: string | null;
}

interface WorksheetResultsResponse {
  items: ScanResultResponse[];
  total: number;
  worksheetId: string;
  worksheetTitle: string;
}

interface OutcomeScanResponse {
  scanId: string;
  sheetNumber: string;
  classId: string | null;
  worksheetId: string;
  worksheetTitle: string;
  percentage: number;
  totalScore: number;
  maxScore: number;
  createdAt: string | null;
}

interface OutcomeResultsResponse {
  items: OutcomeScanResponse[];
  total: number;
  outcomeId: string;
  worksheetCount: number;
}

interface WorksheetSummaryResponse {
  worksheetId: string;
  worksheetTitle: string;
  grade: string | null;
  subject: string | null;
  outcomeIds: string[];
  scanCount: number;
  avgPercentage: number | null;
}

interface SummaryResponse {
  items: WorksheetSummaryResponse[];
  total: number;
}

// =============================================================================
// Types - Frontend (camelCase)
// =============================================================================

export interface ScanResult {
  scanId: string;
  sheetNumber: string;
  classId: string | null;
  totalScore: number;
  maxScore: number;
  percentage: number;
  requiresReview: boolean;
  reviewedByTeacher: boolean;
  createdAt: string | null;
}

export interface WorksheetResults {
  items: ScanResult[];
  total: number;
  worksheetId: string;
  worksheetTitle: string;
}

export interface OutcomeScan {
  scanId: string;
  sheetNumber: string;
  classId: string | null;
  worksheetId: string;
  worksheetTitle: string;
  percentage: number;
  totalScore: number;
  maxScore: number;
  createdAt: string | null;
}

export interface OutcomeResults {
  items: OutcomeScan[];
  total: number;
  outcomeId: string;
  worksheetCount: number;
}

export interface WorksheetSummary {
  worksheetId: string;
  worksheetTitle: string;
  grade: string | null;
  subject: string | null;
  outcomeIds: string[];
  scanCount: number;
  avgPercentage: number | null;
}

export interface ResultsSummary {
  items: WorksheetSummary[];
  total: number;
}

// =============================================================================
// Service
// =============================================================================

class ResultsService {
  /**
   * Get all completed scan results for a specific worksheet.
   *
   * @param worksheetId - The worksheet ID to get results for
   * @param classId - Optional class ID to filter results by
   * @param limit - Maximum number of results (default 100, max 500)
   * @param signal - AbortController signal for cancellation
   */
  async getResultsByWorksheet(
    worksheetId: string,
    classId?: string | null,
    limit: number = 100,
    signal?: AbortSignal
  ): Promise<WorksheetResults> {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (classId) {
      params.set('class_id', classId);
    }

    const response = await api.get<WorksheetResultsResponse>(
      `/results/by-worksheet/${encodeURIComponent(worksheetId)}?${params.toString()}`,
      { signal }
    );
    return response;
  }

  /**
   * Get all scan results that test a specific curriculum outcome.
   *
   * @param outcomeId - The curriculum outcome ID (e.g., "SOC_Grade1_O1")
   * @param classId - Optional class ID to filter results by
   * @param limit - Maximum number of results (default 100, max 500)
   * @param signal - AbortController signal for cancellation
   */
  async getResultsByOutcome(
    outcomeId: string,
    classId?: string | null,
    limit: number = 100,
    signal?: AbortSignal
  ): Promise<OutcomeResults> {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (classId) {
      params.set('class_id', classId);
    }

    const response = await api.get<OutcomeResultsResponse>(
      `/results/by-outcome/${encodeURIComponent(outcomeId)}?${params.toString()}`,
      { signal }
    );
    return response;
  }

  /**
   * Get aggregate statistics for all worksheets.
   * Useful for dashboard overview and progress tracking.
   *
   * @param signal - AbortController signal for cancellation
   */
  async getResultsSummary(signal?: AbortSignal): Promise<ResultsSummary> {
    const response = await api.get<SummaryResponse>('/results/summary', { signal });
    return response;
  }
}

export const resultsService = new ResultsService();

/**
 * Check if an error is a request cancellation.
 */
export function isRequestCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  // Axios cancellation
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as { code: string }).code === 'ERR_CANCELED';
  }
  return false;
}
